<template>
    <ModalWindow
      ref="modal_element"
      class="message-move"
      :title="title_text"
      :section_state="folders_controller.section_state"
      section_state_id="fetch_all"
      @modalWindowClose="onModalClose()"
    >
        <template #pre-body v-if="folders_collapse_collection.count > 0">
          <InputSearch
              ref="input_search_component"
              class="contacts-search"
              @inputSearchSet='onInputSearchSet'
              @inputSearchClear='onInputSearchClear'
          />
        </template>

        <template #body>
            <FoldersListCollapsable v-if="folders_search_collection.search_active === false && folders_collapse_collection.count > 0"
              ref="folders_collapse_element"
              selection_box="radio"
              list_type="selection"
              :folders_collapse_collection='folders_collapse_collection'
              :folders_selection_collection='folders_selection_collection'
              @folderSelect="onFolderSelect"
              @folderUnselect="onFolderUnselect"
            />

            <FoldersListSearch v-else-if="folders_search_collection.count > 0"
              selection_box="radio"
              list_type="selection"
              :folders_search_collection='folders_search_collection'
              :folders_selection_collection='folders_selection_collection'
              @folderSelect="onFolderSelect"
              @folderUnselect="onFolderUnselect"
            />

            <ContentEmpty v-else
              text="folders.empty_folders"
              icon="fas fa-folder"
            />
        </template>

        <template #post-body v-if="add_folder_visible">
            <div class="create-folder">
                <div class="form">
                    <fieldset>
                        <div class="control-group">
                            <div class="control">
                                <label for="new_folder_name">
                                    {{Language.say((folders_selection_collection.count) ? 'folders.actions.add_subfolder' : 'folders.actions.add_folder')}}
                                </label>
                                <div class="close" @click="onAddFolderToggle()"><span></span></div>
                                <span class="attach-button">
                                    <input
                                      v-focus="true"
                                      v-model="new_folder_name"
                                      @keyup.enter="onAddFolderSubmit()"
                                      id="new_folder_name" type="text"
                                    >
                                    <ActionButton
                                        @click="onAddFolderSubmit()"
                                        icon="fas fa-check"
                                        class='main-action'
                                        text="messages.move.new_folder_submit"
                                        :loading='folders_controller.saving_state.saving["folders_create"]'
                                        :disabled='!new_folder_name'
                                    />
                                </span>
                            </div>
                        </div>
                  </fieldset>
                </div>
            </div>
        </template>

        <template #footer>
            <ActionButton v-if="folders_create === true"
                @click="onAddFolderToggle()"
                class='outline push-left'
                icon="fas fa-folder-plus"
                :disabled="allow_subfolder === false || add_folder_visible"
                :text="(folders_selection_collection.count) ? 'folders.actions.add_subfolder' : 'folders.actions.add_folder'"
              />

             <ActionButton
                @click="onFolderSubmit()"
                icon="fa fa-folder-open"
                class='main-action push-right'
                :disabled="!folders_selection_collection.count || add_folder_visible"
                :text="button_submit_text"
            />
        </template>
    </ModalWindow>
</template>

<script setup>
  import {ref, nextTick, reactive, } from 'vue';
  import {defineEmits, defineProps } from 'vue';
  import {onBeforeMount}             from 'vue';
  import FoldersListCollapsable      from '../Partials/FoldersListCollapsable.vue';
  import FoldersListSearch           from '../Partials/FoldersListSearch.vue';
  import ModalWindow                 from '@/vue/Layouts/Modals/ModalWindow.vue';
  import ActionButton                from '@/vue/Elements/Buttons/ActionButton.vue'
  import InputSearch                 from '@/vue/Elements/Inputs/InputSearch.vue'
  import ContentEmpty                from '@/vue/Elements/Content/ContentEmpty.vue';
  import FoldersController           from '@/js/Mailboxes/FoldersController';
  import FoldersCollapseCollection   from '@/js/Mailboxes/Collections/FoldersCollapseCollection';
  import FoldersSearchCollection     from '@/js/Mailboxes/Collections/FoldersSearchCollection';
  import CollectionList              from '@/js/Shared/Collections/CollectionList';
  import Language                    from '@/js/Shared/Language/Language.js';

  const emit  = defineEmits([
    'folderSubmit', 'folderSubmitCancel'
  ]);

  const props = defineProps({
    folders_create : {
      default : true,
    },

    title_text : {
      default: 'folders.select_folders',
    },

    button_submit_text : {
      default: 'global.actions.select'
    }
  })

  const modal_element            = ref();
  const input_search_component   = ref();
  const folders_collapse_element = ref();
  const add_folder_visible       = ref(false);
  const allow_subfolder          = ref(true);
  const new_folder_name          = ref();

  const folders_controller           = FoldersController
  const folders_search_collection    = reactive(new FoldersSearchCollection());
  const folders_collapse_collection  = reactive(new FoldersCollapseCollection('move_modal'));
  const folders_selection_collection = reactive(new CollectionList('id'));

  onBeforeMount(() => {
    folders_controller.section_state.onLoad('fetch_all', async () => {
      setCollectionLists();
    })
  });

  const onFolderSelect = (folder) => {
    setAddFolderVisibility(false);
    setAllowSubFolder(folder.main ? false : true);
  }

  const onFolderUnselect = (folder) => {
    setAddFolderVisibility(false);
    setAllowSubFolder(true);
  }

  const onFolderCollapse = () => {
    setAddFolderVisibility(false);
    setAllowSubFolder(true);
  }

  const onInputSearchSet = (term) => {
    folders_search_collection.search(term);
  }

  const onInputSearchClear = (term) => {
    folders_search_collection.reset();
  }

  const onAddFolderToggle = () => {
    setAddFolderVisibility(!add_folder_visible.value)
  }

  const onAddFolderSubmit = async () => {
    let success = await createFolder();

    if (success === false) {
      return;
    }

    folders_search_collection.reset();
    input_search_component.value.resetInput();
    setAddFolderVisibility(false);
    setCollectionLists();
    resetNewFolderName();
    addToViewPort()
  }

  const onFolderSubmit = () => {
    emit('folderSubmit', folders_selection_collection.getFirst());
  }

  const onModalClose = () => {
    emit('folderSubmitCancel');
  }

  const setCollectionLists = () => {
    folders_collapse_collection.setFolders(folders_controller.mailboxes.folders.all);
    folders_search_collection.setSearchList(folders_controller.mailboxes.folders.all);
  }

  const setAllowSubFolder = (value) => {
    allow_subfolder.value = value;
  }

  const resetNewFolderName = () => {
    new_folder_name.value = null;
  }

  const setAddFolderVisibility = (value) => {
    add_folder_visible.value = value;
  }

  const createFolder = async () => {
    return await folders_controller.create(
      new_folder_name.value,
      folders_selection_collection.getFirst()
    );
  }

  const addToViewPort = async () => {
    let selected = folders_selection_collection.getFirst();
    let folder   = folders_controller.mailboxes.folders.all.getLastAdded();

    if (folder.parent) {
      folders_collapse_collection.expand(selected.id);
    }

    folders_selection_collection.unique(folder);
    setAllowSubFolder(folder.main ? false : true);
    await nextTick();
    await nextTick();
    folders_collapse_element.value.scrollToItem(folder);
  }
</script>
